import { useState } from 'react';
import { NetflixRow, NetflixCard } from './NetflixRow';
import { CardDetailModal } from './CardDetailModal';

export const ProjectsRow = () => {
  const [selectedCard, setSelectedCard] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  const projects = [
    {
      title: "FedRAMP 20x Automation",
      subtitle: "Google Cloud • 2023-2025",
      description: "Revolutionary project reducing FedRAMP authorization timelines by 20% through OSCAL-based machine-readable control evidence. Implemented automated compliance monitoring using Google Cloud Compliance Manager, transforming manual document-heavy processes into continuous, automated validation workflows. Directly contributed to FedRAMP 20x initiative modernization.",
      match: "Strategic Impact",
      tags: ["OSCAL", "FedRAMP 20x", "Compliance Automation"],
      imageSrc: "https://images.unsplash.com/photo-1611722341858-acb609fb3d71?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwxfHxjbG91ZCUyMGNvbXBsaWFuY2V8ZW58MHx8fGJsdWV8MTc2MDM0MDM4MXww&ixlib=rb-4.1.0&q=85",
      impact: "$10M+ in accelerated government cloud adoption revenue and industry transformation"
    },
    {
      title: "Gemini AI Safety Framework",
      subtitle: "Google AI • 2024-2025",
      description: "Comprehensive AI governance implementation for Google's Gemini and GenAI products. Established AI model traceability systems, production approval gates for safety-critical deployments, and risk categorization frameworks aligned with EU AI Act requirements. Ensured responsible AI deployment across all Google AI products at global scale.",
      match: "Innovation Leader",
      tags: ["AI Governance", "EU AI Act", "Gemini"],
      imageSrc: "https://images.unsplash.com/photo-1660165458059-57cfb6cc87e5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwyfHxhcnRpZmljaWFsJTIwaW50ZWxsaWdlbmNlfGVufDB8fHxibHVlfDE3NjAzNDAzODZ8MA&ixlib=rb-4.1.0&q=85",
      impact: "Full coverage across all GCP AI/ML products ensuring regulatory compliance and safety"
    },
    {
      title: "Financial Controls Risk Transformation",
      subtitle: "Discover Financial Services • 2019-2021",
      description: "Comprehensive financial risk transformation strengthening controls around lending approvals, credit decisioning, and payment processing workflows. Implemented automated risk monitoring, enhanced approval gateways, and established continuous controls monitoring across all financial product lines, significantly reducing audit deficiencies and regulatory exposure.",
      match: "Operational Excellence",
      tags: ["Financial Controls", "Risk Automation", "Audit"],
      imageSrc: "https://images.unsplash.com/photo-1590494165264-1ebe3602eb80?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwyfHxjeWJlcnNlY3VyaXR5fGVufDB8fHxibHVlfDE3NjAzNDAzOTZ8MA&ixlib=rb-4.1.0&q=85",
      impact: "60% reduction in audit deficiencies and comprehensive financial risk exposure mitigation"
    },
    {
      title: "Shared Fate Security Model",
      subtitle: "Google Cloud Platform • 2023-2024",
      description: "Led comprehensive risk assessment and mitigation strategy for concentration risk under Google's 'Shared Fate' security model. Identified systemic dependencies across regulated workloads, aligned cloud service teams to diversify control ownership, and reduced multi-tenant blast radius through innovative architectural solutions.",
      match: "Risk Leadership",
      tags: ["Shared Fate", "Concentration Risk", "Multi-Tenant"],
      imageSrc: "https://images.unsplash.com/photo-1655993810480-c15dccf9b3a0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwzfHx0ZWNobm9sb2d5JTIwbmV0d29ya3xlbnwwfHx8Ymx1ZXwxNzYwMzQwNDAxfDA&ixlib=rb-4.1.0&q=85",
      impact: "75% reduction in systemic risk dependencies across regulated customer workloads"
    },
    {
      title: "LinkedIn Common Controls Program",
      subtitle: "LinkedIn • 2022",
      description: "Scaled LinkedIn's Common Controls Program achieving 30% increased adoption across engineering teams while reducing audit preparation time by 25%. Implemented structured risk controls and audit-aligned frameworks ensuring compliance with ISO 27001, SOX, and GDPR across LinkedIn's global platform serving 800M+ professionals.",
      match: "Program Excellence",
      tags: ["Common Controls", "ISO 27001", "SOX"],
      imageSrc: "https://images.unsplash.com/photo-1488590528505-98d2b5aba04b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwzfHxjeWJlcnNlY3VyaXR5fGVufDB8fHxibHVlfDE3NjAzNDAzOTZ8MA&ixlib=rb-4.1.0&q=85",
      impact: "$2M+ in operational efficiency gains and enterprise-wide compliance cost reduction"
    },
    {
      title: "Discover Cyber Insurance Optimization",
      subtitle: "Discover Financial • 2020-2021",
      description: "Negotiated $125M cyber insurance expansion while achieving $50K premium reduction and adding nation-state threat coverage. Developed EML/PML-based cyber risk quantification frameworks translating technical vulnerabilities into financial exposure estimates, supporting board-level decision-making on risk prioritization and investment strategy.",
      match: "Financial Leadership",
      tags: ["Cyber Insurance", "Risk Quantification", "EML/PML"],
      imageSrc: "https://images.unsplash.com/photo-1597781914467-a5b93258e748?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwxfHxkYXRhJTIwcHJvdGVjdGlvbnxlbnwwfHx8Ymx1ZXwxNzYwMzQwNDA2fDA&ixlib=rb-4.1.0&q=85",
      impact: "$125M enhanced risk coverage with optimized costs and comprehensive threat protection"
    },
    {
      title: "Red Flags Identity Theft Prevention",
      subtitle: "Discover Financial • 2018-2020",
      description: "Engineered and operationalized comprehensive Red Flags Identity Theft Prevention controls integrated into consumer-facing financial products (Cards, Banking & Student Loans). Achieved 30% improvement in early fraud detection and account protection through innovative control design and implementation across all product lines.",
      match: "Security Innovation",
      tags: ["Red Flags", "Fraud Detection", "Financial Products"],
      imageSrc: "https://images.unsplash.com/photo-1581090464777-f3220bbe1b8b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwzfHxhcnRpZmljaWFsJTIwaW50ZWxsaWdlbmNlfGVufDB8fHxibHVlfDE3NjAzNDAzODZ8MA&ixlib=rb-4.1.0&q=85",
      impact: "30% improvement in fraud detection with millions in prevented losses annually"
    },
    {
      title: "NYDFS Cybersecurity Compliance",
      subtitle: "Discover Financial • 2019-2020",
      description: "Secured NYDFS cybersecurity approval for Discover's new banking entity with zero findings, showcasing mature InfoSec governance and risk program capabilities. Comprehensive preparation included policy alignment, control implementation, risk assessment documentation, and regulatory engagement resulting in flawless approval process.",
      match: "Regulatory Excellence",
      tags: ["NYDFS", "Banking Compliance", "Zero Findings"],
      imageSrc: "https://images.unsplash.com/photo-1560435569-6f26b90683e2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwyfHxjbG91ZCUyMGNvbXBsaWFuY2V8ZW58MHx8fGJsdWV8MTc2MDM0MDM4MXww&ixlib=rb-4.1.0&q=85",
      impact: "Enabled $500M+ banking expansion with perfect regulatory compliance record"
    }
  ];

  const handleCardClick = (project) => {
    setSelectedCard(project);
    setModalOpen(true);
  };

  return (
    <>
      <NetflixRow title="Major Projects & Initiatives" id="projects">
        {projects.map((project, index) => (
          <NetflixCard
            key={index}
            title={project.title}
            subtitle={project.subtitle}
            description={project.description}
            match={project.match}
            tags={project.tags}
            imageSrc={project.imageSrc}
            onClick={() => handleCardClick(project)}
          />
        ))}
      </NetflixRow>
      
      <CardDetailModal 
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        card={selectedCard}
        type="project"
      />
    </>
  );
};